import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLogin } from '@/api/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useLanguage } from '@/i18n/LanguageContext'
import { isForexReview, isReviewMode } from '@/lib/staging'
import { BarChart3, Shield, Code } from 'lucide-react'

export function Login() {
  const { t } = useLanguage()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const login = useLogin()
  const navigate = useNavigate()
  const forex = isForexReview()
  const review = isReviewMode()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    login.mutate(
      { username, password },
      {
        onSuccess: (data) => {
          navigate(review ? '/gerenciador' : data.user.isAdmin ? '/admin' : '/painel')
        },
        onError: () => {
          setError(forex ? 'Invalid username or password' : t('Usuario ou senha incorretos'))
        },
      }
    )
  }

  const accentColor = forex ? '#1877F2' : '#c9a84c'
  const accentGradient = forex
    ? 'linear-gradient(135deg, #1877F2 0%, #42a5f5 50%, #1877F2 100%)'
    : 'linear-gradient(135deg, #c9a84c 0%, #e8d48b 50%, #c9a84c 100%)'
  const accentGradientSimple = forex
    ? 'linear-gradient(135deg, #1877F2 0%, #42a5f5 100%)'
    : 'linear-gradient(135deg, #c9a84c 0%, #e8d48b 100%)'
  const buttonGradient = forex
    ? 'linear-gradient(135deg, #1877F2 0%, #1565C0 100%)'
    : 'linear-gradient(135deg, #c9a84c 0%, #b8943e 100%)'

  return (
    <div className="relative flex min-h-screen flex-col lg:flex-row bg-background">
      {/* ─── LEFT PANEL: Brand ─── */}
      <div
        className="relative flex flex-col justify-between overflow-hidden px-8 py-10 lg:w-[45%] lg:min-h-screen lg:px-12 lg:py-14"
        style={{
          background: forex
            ? 'linear-gradient(160deg, #0a1628 0%, #0d1117 40%, #0f1923 100%)'
            : 'linear-gradient(160deg, #12110f 0%, #0a0a0c 40%, #111113 100%)',
        }}
      >
        {/* Decorative geometric shapes */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="absolute -right-24 -top-24 h-72 w-72 rounded-full border opacity-[0.07] lg:h-[420px] lg:w-[420px] lg:-right-32 lg:-top-32"
            style={{ borderColor: accentColor }}
          />
          <div
            className="absolute -bottom-10 -left-10 h-40 w-40 rounded-full opacity-[0.05] lg:h-56 lg:w-56 lg:-bottom-16 lg:-left-16"
            style={{ background: accentColor }}
          />
          <div
            className="absolute left-1/2 top-1/3 hidden h-px w-64 -rotate-45 opacity-[0.08] lg:block"
            style={{ background: accentColor }}
          />
          <div
            className="absolute right-16 bottom-1/4 hidden h-16 w-16 rotate-45 rounded-sm border opacity-[0.06] lg:block"
            style={{ borderColor: accentColor }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            {forex ? (
              <div className="flex h-10 w-10 lg:h-12 lg:w-12 items-center justify-center rounded-xl" style={{ background: 'linear-gradient(135deg, #1877F2, #1565C0)' }}>
                <BarChart3 className="h-6 w-6 text-white" />
              </div>
            ) : (
              <img src="/images/logo.png" alt="Blissf" className="h-10 w-10 drop-shadow-lg lg:h-12 lg:w-12" />
            )}
            <span className="text-lg font-bold tracking-tight lg:text-xl" style={{ background: accentGradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {forex ? 'ForexBrokerReview' : 'Blissf'}
            </span>
          </div>
        </div>

        <div className="relative z-10 hidden lg:block">
          <h1 className="max-w-sm text-3xl font-bold leading-tight tracking-tight xl:text-4xl" style={{ background: 'linear-gradient(135deg, #e5e2dc 0%, #a8a5a0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {forex ? 'Facebook Page Management Platform' : t('Gerencie todas suas automacoes em um so lugar.')}
          </h1>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">
            {forex ? 'Connect, manage, and engage with your audience using the official Meta Graph API.' : 'Instagram \u2022 Facebook \u2022 YouTube \u2022 WhatsApp'}
          </p>
          {forex && (
            <div className="mt-6 flex flex-col gap-3">
              {[
                { icon: Shield, text: '17 Meta API permissions with full debug output' },
                { icon: Code, text: 'Real-time API responses for every action' },
                { icon: BarChart3, text: 'Page insights, engagement, and messaging' },
              ].map((feat) => (
                <div key={feat.text} className="flex items-center gap-2.5 text-zinc-400 text-sm">
                  <feat.icon className="h-4 w-4 shrink-0" style={{ color: '#1877F2' }} />
                  <span>{feat.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="relative z-10 mt-6 flex items-center gap-6 lg:mt-0 lg:gap-10">
          {(forex ? [
            { value: 'v22.0', label: 'Graph API' },
            { value: '17', label: 'Permissions' },
            { value: 'Real-time', label: 'Debug' },
          ] : [
            { value: '500+', label: t('Contas ativas') },
            { value: '10M+', label: t('Mensagens enviadas') },
            { value: '24/7', label: t('Automacao') },
          ]).map((stat) => (
            <div key={stat.label} className="flex flex-col">
              <span className="text-lg font-bold lg:text-2xl" style={{ background: accentGradientSimple, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {stat.value}
              </span>
              <span className="text-[11px] text-muted-foreground lg:text-xs">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ─── RIGHT PANEL: Form ─── */}
      <div className="flex flex-1 items-center justify-center px-6 py-12 lg:w-[55%] lg:py-0">
        <div className="w-full max-w-[320px]">
          <div className="mb-8">
            <h2 className="text-2xl font-bold tracking-tight text-foreground">
              {forex ? 'Welcome back' : t('Bem-vindo de volta')}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {forex ? 'Sign in to manage your Pages' : t('Entre para gerenciar suas automacoes')}
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username">{forex ? 'Username' : t('Usuario')}</Label>
              <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder={forex ? 'Your username' : t('Seu usuario')} required autoFocus />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{forex ? 'Password' : t('Senha')}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={forex ? 'Your password' : t('Sua senha')} required />
            </div>
            <Button type="submit" className="w-full font-semibold" size="lg" disabled={login.isPending} style={{ background: buttonGradient }}>
              {login.isPending ? (forex ? 'Signing in...' : t('Entrando...')) : (forex ? 'Sign In' : t('Entrar'))}
            </Button>
          </form>

          <p className="mt-8 text-center text-xs text-muted-foreground">
            {forex ? 'Facebook Page Management via Official Meta Graph API' : t('Automacao de Instagram e Facebook')}
          </p>
        </div>
      </div>

      {/* ─── FOOTER: Legal ─── */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-border/40 py-4 px-6">
        <div className="flex flex-wrap items-center justify-center gap-4 text-[11px] text-muted-foreground">
          <a href="/privacy" className="hover:text-foreground transition-colors">{forex ? 'Privacy Policy' : t('Política de Privacidade')}</a>
          <span className="opacity-30">|</span>
          <a href="/terms" className="hover:text-foreground transition-colors">{forex ? 'Terms of Service' : t('Termos de Serviço')}</a>
          <span className="opacity-30">|</span>
          <a href="/suporte" className="hover:text-foreground transition-colors">{forex ? 'Support' : t('Suporte')}</a>
        </div>
        <p className="mt-2 text-center text-[10px] text-muted-foreground/60">
          {forex
            ? <>&copy; 2026 ForexBrokerReview &mdash; All rights reserved. Page management platform powered by the official Meta Graph API.</>
            : <>&copy; 2026 Blissf &mdash; Todos os direitos reservados. Plataforma de automa&ccedil;&atilde;o via API Oficial Meta/Google. <a href="http://linksystem.com.br/" target="_blank" rel="noopener noreferrer" className="hover:text-muted-foreground transition-colors">Desenvolvido por LinkSystem</a></>
          }
        </p>
      </div>
    </div>
  )
}
